interface ExamDateComponents {
  year: number;
  month: number;
  day: number;
}

const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const LEGACY_ISO_TIMESTAMP_PATTERN = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-](\d{2}):(\d{2}))$/;
const DAYS_BEFORE_MONTH = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334] as const;

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  if (month === 4 || month === 6 || month === 9 || month === 11) return 30;
  return 31;
}

function isValidDateComponents({ year, month, day }: ExamDateComponents): boolean {
  return Number.isInteger(year)
    && Number.isInteger(month)
    && Number.isInteger(day)
    && year >= 1
    && year <= 9999
    && month >= 1
    && month <= 12
    && day >= 1
    && day <= daysInMonth(year, month);
}

function parseDateKey(dateKey: string): ExamDateComponents | undefined {
  const match = DATE_KEY_PATTERN.exec(dateKey);
  if (!match) return undefined;

  const components: ExamDateComponents = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
  return isValidDateComponents(components) ? components : undefined;
}

function validatedDateKey(components: ExamDateComponents): string | undefined {
  if (!isValidDateComponents(components)) return undefined;
  return `${String(components.year).padStart(4, '0')}-${String(components.month).padStart(2, '0')}-${String(components.day).padStart(2, '0')}`;
}

function hasValidLegacyTimestampShape(value: string): RegExpExecArray | undefined {
  const match = LEGACY_ISO_TIMESTAMP_PATTERN.exec(value);
  if (!match) return undefined;

  const hour = Number(match[2]);
  const minute = Number(match[3]);
  const second = Number(match[4]);
  const offsetHour = match[5] === undefined ? 0 : Number(match[5]);
  const offsetMinute = match[6] === undefined ? 0 : Number(match[6]);
  if (hour > 23 || minute > 59 || second > 59 || offsetHour > 23 || offsetMinute > 59) {
    return undefined;
  }
  return match;
}

export function extractExamDateKey(value: string | null | undefined): string | undefined {
  if (!value) return undefined;

  const direct = parseDateKey(value);
  if (direct) return value;

  const timestampMatch = hasValidLegacyTimestampShape(value);
  if (!timestampMatch) return undefined;
  const dateKey = timestampMatch[1];
  return parseDateKey(dateKey) ? dateKey : undefined;
}

export function toExamDatePatchValue(input: string): string | null | undefined {
  const trimmed = input.trim();
  if (!trimmed) return null;
  return parseDateKey(trimmed) ? trimmed : undefined;
}

export function europeRomeDateKey(now: Date): string | undefined {
  if (Number.isNaN(now.getTime())) return undefined;

  const parts = new Intl.DateTimeFormat('en-CA-u-ca-gregory-nu-latn', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const values: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const part of parts) values[part.type] = part.value;

  if (values.year === undefined || values.month === undefined || values.day === undefined) {
    return undefined;
  }
  return validatedDateKey({
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
  });
}

export function calendarDayOrdinal(dateKey: string): number | undefined {
  const components = parseDateKey(dateKey);
  if (!components) return undefined;

  const priorYear = components.year - 1;
  const priorYearDays = priorYear * 365
    + Math.floor(priorYear / 4)
    - Math.floor(priorYear / 100)
    + Math.floor(priorYear / 400);
  const leapDay = components.month > 2 && isLeapYear(components.year) ? 1 : 0;
  return priorYearDays + DAYS_BEFORE_MONTH[components.month - 1] + leapDay + components.day - 1;
}

export function calendarDayDifference(fromDateKey: string, toDateKey: string): number | undefined {
  const fromOrdinal = calendarDayOrdinal(fromDateKey);
  const toOrdinal = calendarDayOrdinal(toDateKey);
  if (fromOrdinal === undefined || toOrdinal === undefined) return undefined;
  return toOrdinal - fromOrdinal;
}

export function formatItalianExamDate(value: string | null | undefined): string | null {
  const dateKey = extractExamDateKey(value);
  if (!dateKey) return null;
  const components = parseDateKey(dateKey);
  if (!components) return null;
  return `${String(components.day).padStart(2, '0')}/${String(components.month).padStart(2, '0')}/${String(components.year).padStart(4, '0')}`;
}
